import { zodResolver } from '@hookform/resolvers/zod';
import { useCopy } from '../../lib/useCopy';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type FieldPath,
  type UseFormSetValue,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { Money } from '../../components/Money';
import { ContactPicker, type Contact } from '../../components/pickers';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  purchaseApi,
  useLocations,
  usePurchaseOrder,
  usePurchasesMutation,
  useTaxRates,
  type PurchaseOrder,
  type TaxRate,
} from './api';
import { ItemPicker, TaxSelect, UnitSelect } from './components/items';
import { useOnUnitsReady, useVariantUnits } from './components/units';
import {
  DecimalField,
  DetailSkeleton,
  DiscountInput,
  DocHeader,
  ErrorState,
  TotalsRows,
} from './components/ui';
import { todayIn } from './lib/dates';
import { apiFieldErrors } from './lib/errors';
import {
  defaultUnitPrice,
  emptyOrderForm,
  emptyOrderLine,
  orderFormSchema,
  orderToForm,
  orderTotalsInput,
  rescalePrice,
  toCreateOrderBody,
  toUpdateOrderBody,
  type OrderForm,
} from './lib/forms';
import { computeTotals } from './lib/totals';

/** Create (`/purchases/orders/new`) or edit a draft (`/purchases/orders/:id/edit`). */
export function PurchaseOrderFormPage() {
  const { id } = useParams();
  const order = usePurchaseOrder(id);
  // Duplicate: the copied order's supplier, lines and prices, dated today, as a new draft.
  const copy = useCopy() as PurchaseOrder | null;
  if (id && order.isLoading) return <DetailSkeleton />;
  if (id && order.error)
    return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;
  if (id && order.data && order.data.status !== 'DRAFT')
    return (
      <Alert severity="info">
        {order.data.number} is {order.data.status.toLowerCase().replace('_', ' ')} — only drafts can
        be edited.
      </Alert>
    );
  return <OrderEditor key={order.data?.id ?? copy?.id ?? 'new'} order={order.data} copy={copy} />;
}

function OrderEditor({ order, copy }: { order?: PurchaseOrder; copy?: PurchaseOrder | null }) {
  const { can } = useAuth();
  const { settings, format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const taxEnabled = settings?.taxEnabled ?? false;
  const taxRates = useTaxRates();
  const locations = useLocations();
  const today = todayIn(format.timeZone);
  const [pending, setPending] = useState<'confirm' | 'save' | null>(null);

  const form = useForm<OrderForm>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: order
      ? orderToForm(order)
      : copy
        ? { ...orderToForm(copy), docDate: today, expectedDate: '', reference: '' }
        : emptyOrderForm(today),
    mode: 'onTouched',
  });
  const { control, handleSubmit, setError, setValue, formState } = form;
  const lines = useFieldArray({ control, name: 'lines', keyName: 'fid' });

  const values = useWatch({ control });
  const rates = useMemo(
    () => new Map((taxRates.data ?? []).map((r) => [r.id, r.rate])),
    [taxRates.data],
  );
  const totals = useMemo(
    () =>
      computeTotals(
        orderTotalsInput((values.lines ?? []) as OrderForm['lines'], (id) => rates.get(id)),
        {
          decimals: format.currencyDecimals,
          taxEnabled,
          pricesIncludeTax: order?.pricesIncludeTax ?? settings?.pricesIncludeTax ?? false,
          documentDiscount:
            values.discountValue && /^\d/.test(values.discountValue)
              ? { type: values.discountType ?? 'AMOUNT', value: values.discountValue }
              : null,
        },
      ),
    [values, rates, format.currencyDecimals, taxEnabled, order, settings],
  );

  const save = usePurchasesMutation(({ v, confirm }: { v: OrderForm; confirm: boolean }) =>
    order
      ? purchaseApi
          .updateOrder(order.id, toUpdateOrderBody(v, { taxEnabled, version: order.version }))
          .then((po) => (confirm ? purchaseApi.confirmOrder(po.id, po.version) : po))
      : purchaseApi.createOrder(toCreateOrderBody(v, { taxEnabled, confirm })),
  );

  const submit = (confirm: boolean) => {
    if (save.isPending) return;
    setPending(confirm ? 'confirm' : 'save');
    void handleSubmit(async (v) => {
      try {
        const po = await save.mutateAsync({ v, confirm });
        notify.success(
          confirm
            ? `${po.number} confirmed`
            : order
              ? `${po.number} saved`
              : `${po.number} saved as draft`,
        );
        void navigate(`/purchases/orders/${po.id}`, { replace: true });
      } catch (e) {
        const fields = apiFieldErrors(e);
        for (const f of fields)
          setError(f.field as FieldPath<OrderForm>, { type: 'server', message: f.message });
        notify.error(e);
      }
    })().finally(() => setPending(null));
  };

  // ⌘S / Ctrl+S saves the draft
  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        submitRef.current(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const linesError = formState.errors.lines?.root?.message ?? formState.errors.lines?.message;

  return (
    <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
      <DocHeader
        back={
          order
            ? { to: `/purchases/orders/${order.id}`, label: order.number }
            : { to: '/purchases', label: 'Purchases' }
        }
        eyebrow={order ? 'Edit draft' : 'New purchase order'}
        title={order ? order.number : copy ? `Copy of ${copy.number}` : 'Order from a supplier'}
        subtitle="Prices are your cost per unit. Nothing enters stock until goods are received."
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 340px' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Card sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Supplier & delivery
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', md: '2fr 1fr 1fr' },
              }}
            >
              <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' } }}>
                <Controller
                  control={control}
                  name="supplier"
                  render={({ field, fieldState }) => (
                    <ContactPicker
                      type="supplier"
                      value={field.value as Contact | null}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
              <Controller
                control={control}
                name="docDate"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Order date"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="expectedDate"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Expected delivery"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              {locations.data && locations.data.length > 1 && (
                <Controller
                  control={control}
                  name="locationId"
                  render={({ field }) => (
                    <TextField select label="Deliver to" {...field}>
                      <MenuItem value="">Default location</MenuItem>
                      {locations.data.map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}
              <Controller
                control={control}
                name="reference"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Supplier quote / reference"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </Card>

          <Card sx={{ mb: 3 }}>
            <Stack
              direction="row"
              sx={{
                px: 2.5,
                py: 1.75,
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6">Items</Typography>
              <Typography variant="body2" color="text.secondary">
                {lines.fields.length} {lines.fields.length === 1 ? 'line' : 'lines'}
              </Typography>
            </Stack>
            <Box>
              {lines.fields.map((f, i) => (
                <OrderLineRow
                  key={f.fid}
                  index={i}
                  control={control}
                  setValue={setValue}
                  taxEnabled={taxEnabled}
                  taxRates={taxRates.data ?? []}
                  lineTotal={totals.lines[i]?.total}
                  lineError={totals.lines[i]?.error}
                  canRemove={lines.fields.length > 1}
                  onRemove={() => lines.remove(i)}
                  autoFocus={i > 0 && i === lines.fields.length - 1}
                />
              ))}
            </Box>
            <Box sx={{ px: 2.5, py: 2 }}>
              {linesError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {linesError}
                </Alert>
              )}
              <Button startIcon={<AddRounded />} onClick={() => lines.append(emptyOrderLine())}>
                Add item
              </Button>
            </Box>
          </Card>

          <Card sx={{ p: 2.5 }}>
            <Controller
              control={control}
              name="notes"
              render={({ field }) => (
                <TextField {...field} label="Notes for the supplier" multiline minRows={2} />
              )}
            />
          </Card>
        </Box>

        <Card
          sx={{
            p: 2.5,
            position: { lg: 'sticky' },
            top: { lg: 88 },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Order total
          </Typography>
          <Controller
            control={control}
            name="discountValue"
            render={({ field, fieldState }) => (
              <Controller
                control={control}
                name="discountType"
                render={({ field: typeField }) => (
                  <DiscountInput
                    label="Order discount"
                    type={typeField.value}
                    value={field.value}
                    onType={typeField.onChange}
                    onValue={field.onChange}
                    error={fieldState.error?.message ?? totals.error}
                  />
                )}
              />
            )}
          />
          <Box sx={{ mt: 2 }}>
            <TotalsRows
              rows={[
                { label: 'Subtotal', value: totals.subtotal },
                ...(Number(totals.discountTotal) > 0
                  ? [{ label: 'Discounts', value: totals.discountTotal, negative: true }]
                  : []),
                ...(taxEnabled ? [{ label: 'Tax', value: totals.taxTotal }] : []),
              ]}
              total={totals.total}
            />
          </Box>
          {(order?.pricesIncludeTax ?? settings?.pricesIncludeTax) && taxEnabled && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Prices include tax.
            </Typography>
          )}
          <Divider sx={{ my: 2.5 }} />
          <Stack spacing={1.25}>
            {can('purchases.confirm') && (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircleRounded />}
                loading={pending === 'confirm'}
                disabled={!!pending}
                onClick={() => submit(true)}
              >
                Save & confirm
              </Button>
            )}
            <Tooltip title="⌘S / Ctrl+S">
              <Button
                variant={can('purchases.confirm') ? 'outlined' : 'contained'}
                size="large"
                startIcon={<SaveRounded />}
                loading={pending === 'save'}
                disabled={!!pending}
                onClick={() => submit(false)}
              >
                {order ? 'Save draft' : 'Save as draft'}
              </Button>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            A confirmed order can be received and billed. Drafts stay editable.
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}

function OrderLineRow({
  index,
  control,
  setValue,
  taxEnabled,
  taxRates,
  lineTotal,
  lineError,
  canRemove,
  onRemove,
  autoFocus,
}: {
  index: number;
  control: Control<OrderForm>;
  setValue: UseFormSetValue<OrderForm>;
  taxEnabled: boolean;
  taxRates: TaxRate[];
  lineTotal?: string;
  lineError?: string;
  canRemove: boolean;
  onRemove: () => void;
  autoFocus?: boolean;
}) {
  const { can } = useAuth();
  const line = useWatch({ control, name: `lines.${index}` });
  const { units } = useVariantUnits(line.variant);
  const name = <K extends keyof OrderForm['lines'][number]>(k: K) => `lines.${index}.${k}` as const;

  useOnUnitsReady(line.variant?.id, units, !line.unitId, (u) => {
    setValue(name('unitId'), u.unitId);
    setValue(name('unitSymbol'), u.symbol);
    setValue(name('factor'), u.factor);
    if (!line.unitPrice)
      setValue(name('unitPrice'), defaultUnitPrice(line.variant?.purchasePrice, u.factor));
  });

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'grid',
        gap: 1.5,
        alignItems: 'start',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0,1fr))',
          md: taxEnabled
            ? '0.8fr 0.7fr 1fr 1.4fr 0.9fr minmax(130px,auto)'
            : '0.8fr 0.7fr 1fr 1.4fr minmax(130px,auto)',
        },
      }}
    >
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <Controller
          control={control}
          name={name('variant')}
          render={({ field, fieldState }) => (
            <ItemPicker
              value={field.value}
              autoFocus={autoFocus}
              error={fieldState.error?.message}
              onChange={(v) => {
                field.onChange(v);
                setValue(name('unitId'), '');
                setValue(name('unitSymbol'), '');
                setValue(name('factor'), '1');
                setValue(name('unitPrice'), '');
                setValue(name('description'), v?.displayName ?? '');
              }}
            />
          )}
        />
      </Box>
      <UnitSelect
        units={units}
        value={line.unitId}
        fallbackSymbol={line.unitSymbol}
        onChange={(u) => {
          setValue(name('unitPrice'), rescalePrice(line.unitPrice, line.factor, u.factor));
          setValue(name('unitId'), u.unitId);
          setValue(name('unitSymbol'), u.symbol);
          setValue(name('factor'), u.factor);
        }}
      />
      <Controller
        control={control}
        name={name('qty')}
        render={({ field, fieldState }) => (
          <DecimalField
            {...field}
            label="Qty"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name={name('unitPrice')}
        render={({ field, fieldState }) => (
          <DecimalField
            {...field}
            label="Unit cost"
            placeholder={can('products.view_cost') ? 'Last cost' : 'Default'}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name={name('discountValue')}
        render={({ field, fieldState }) => (
          <DiscountInput
            type={line.discountType}
            value={field.value}
            onType={(t) => setValue(name('discountType'), t)}
            onValue={field.onChange}
            error={fieldState.error?.message ?? lineError}
          />
        )}
      />
      {taxEnabled && (
        <Controller
          control={control}
          name={name('taxRateId')}
          render={({ field }) => (
            <TaxSelect rates={taxRates} value={field.value} onChange={field.onChange} />
          )}
        />
      )}
      <Box
        sx={{
          textAlign: 'right',
          pt: { xs: 0, md: 1 },
          gridColumn: { xs: '1 / -1', md: 'auto' },
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: { md: 'none' } }}>
          Line total
        </Typography>
        <Typography sx={{ fontWeight: 650 }}>
          <Money value={lineTotal ?? '0'} />
        </Typography>
        <Box>
          <Tooltip title="Remove line">
            <span>
              <Button
                size="small"
                color="inherit"
                onClick={onRemove}
                disabled={!canRemove}
                sx={{ minWidth: 0, color: 'text.secondary' }}
                aria-label="Remove line"
              >
                <DeleteOutlineRounded fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
